import { skipToken, useInfiniteQuery } from '@tanstack/react-query';
import { getV1BillingInvoices } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback, useMemo } from 'react';

import { BillingInvoice } from '~hooks/useBilling/models';
import connection from '~services/connectionModule';
import { extractPagination, Pagination } from '~services/pagination';

const INVOICES_PAGE_LIMIT = 20;

interface InvoicesPage {
  invoices: BillingInvoice[];
  pagination: Pagination;
}

export default function useBillingInvoicesInfiniteQuery(
  companyId: string,
  enabled: boolean,
) {
  const { data, fetchNextPage, isError, isFetching, isFetchingNextPage } =
    useInfiniteQuery({
      // companyId in the key gives a platform admin's company switch its own cache
      // entry, so the new company's first page is never fetched with a cursor left
      // over from the previous company.
      queryKey: ['billingInvoices', companyId],
      initialPageParam: undefined as Pagination | undefined,
      getNextPageParam: (lastPage: InvoicesPage) =>
        lastPage.pagination.after ? lastPage.pagination : undefined,
      queryFn:
        enabled && companyId
          ? async ({ pageParam, signal }): Promise<InvoicesPage> => {
              try {
                const response = await getV1BillingInvoices({
                  path: { 'company-id': companyId },
                  query: {
                    'page[limit]': INVOICES_PAGE_LIMIT,
                    'page[after]': pageParam?.after,
                  },
                  signal,
                });

                return {
                  invoices: response.data.data.map(BillingInvoice.parse),
                  pagination: extractPagination(response),
                };
              } catch (error) {
                connection.handleRequestError(
                  error,
                  t('error_messages.billing.failed_to_fetch_invoices'),
                );
                throw error;
              }
            }
          : skipToken,
    });

  const invoices = useMemo(
    () => (data?.pages ?? []).flatMap((page) => page.invoices),
    [data?.pages],
  );

  const hasMore = Boolean(data?.pages.at(-1)?.pagination.after);

  const fetchMore = useCallback(() => {
    if (hasMore) {
      fetchNextPage();
    }
  }, [hasMore, fetchNextPage]);

  return { fetchMore, hasMore, invoices, isError, isFetching, isFetchingNextPage };
}
